import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Badge, ProgressBar, SectionDivider } from './shared';

const PIE_COLORS = ['#1f58a4','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#dc2626'];

function DonutChart({ slices, poolsLabel }) {
  const total = slices.reduce((s, sl) => s + sl.pct, 0) || 100;
  let cumAngle = -90;
  const toXY = (angle, r) => [50 + r * Math.cos((angle * Math.PI) / 180), 50 + r * Math.sin((angle * Math.PI) / 180)];
  const paths = slices.map((sl, i) => {
    const sweep = (sl.pct / total) * 360;
    const startAngle = cumAngle; cumAngle += sweep;
    const [x1, y1] = toXY(startAngle, 38), [x2, y2] = toXY(cumAngle, 38);
    return (
      <path key={i} d={`M 50 50 L ${x1} ${y1} A 38 38 0 ${sweep > 180 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={sl.color} opacity={0.9}>
        <title>{sl.label}: {sl.percent}</title>
      </path>
    );
  });
  return (
    <svg viewBox="0 0 100 100" style={{ width: 110, height: 110, flexShrink: 0 }}>
      {paths}
      <circle cx="50" cy="50" r="22" fill="#ffffff" />
      <text x="50" y="53" textAnchor="middle" fill="#4a6a8a" fontSize="9" fontWeight="700">{slices.length} {poolsLabel}</text>
    </svg>
  );
}

function scoreToColor(score) {
  if (!score) return '#6b8dad';
  if (score.includes('🟢') || /healthy/i.test(score)) return '#059669';
  if (score.includes('🔴') || /high/i.test(score)) return '#dc2626';
  return '#d97706';
}

export default function TokenomicsCard({ data }) {
  const { t } = useTranslation();
  const slices = data.distribution.map((d, i) => ({ ...d, pct: parseFloat(d.percent) || 0, color: PIE_COLORS[i % PIE_COLORS.length] })).filter(d => d.pct > 0);
  const scoreColor = scoreToColor(data.score);

  return (
    <Card title="Tokenomics" icon="🪙">
      <div style={s.supplyRow}>
        {[
          { label: t('tokenomicsCard.totalSupply'), value: data.totalSupply },
          { label: t('tokenomicsCard.circulating'), value: data.circulating },
          { label: 'FDV (TGE)',      value: data.fdvAtLaunch },
          { label: 'MCap (TGE)',     value: data.mcapAtTge },
        ].filter(x => x.value).map(x => (
          <div key={x.label} style={s.supplyItem}>
            <span style={s.supplyLabel}>{x.label}</span>
            <span style={s.supplyValue}>{x.value}</span>
          </div>
        ))}
      </div>

      <SectionDivider label={t('tokenomicsCard.distribution')} />

      {slices.length > 0 && (
        <div style={s.distRow}>
          <DonutChart slices={slices} poolsLabel={t('tokenomicsCard.pools')} />
          <div style={s.legend}>
            {slices.map((sl, i) => (
              <div key={i} style={s.legendRow}>
                <span style={{ ...s.legendDot, background: sl.color }} />
                <span style={s.legendLabel}>{sl.label}</span>
                <span style={s.legendPct}>{sl.percent}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.distribution.length > 0 && (
        <div style={s.vestTable}>
          {data.distribution.map((d, i) => (
            <div key={i} style={s.vestRow}>
              <div style={s.vestLeft}>
                <span style={{ ...s.vestDot, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span style={s.vestLabel}>{d.label}</span>
              </div>
              <div style={s.vestRight}>
                <span style={s.vestPct}>{d.percent}</span>
                <ProgressBar pct={parseFloat(d.percent)} color={PIE_COLORS[i % PIE_COLORS.length]} />
                <span style={s.vestVesting}>{d.vesting}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.utility?.length > 0 && (
        <div style={s.utilRow}>
          <SectionDivider label={t('tokenomicsCard.utility')} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.utility.map((u, i) => <Badge key={i} label={u} color="#1f58a4" size="sm" />)}
          </div>
        </div>
      )}

      {data.update && Object.values(data.update).some(Boolean) && (
        <div style={s.updateRow}>
          <SectionDivider label={t('tokenomicsCard.updateSection')} />
          {[
            { label: t('tokenomicsCard.outdated'),       value: data.update.outdated },
            { label: t('tokenomicsCard.unlockProgress'), value: data.update.unlockProgress },
            { label: t('tokenomicsCard.lockChanges'),    value: data.update.lockChanges },
            { label: t('tokenomicsCard.newBurn'),        value: data.update.newBurn },
            { label: t('tokenomicsCard.addedUtilities'), value: data.update.addedUtilities },
            { label: t('tokenomicsCard.thesis'),         value: data.update.thesis },
          ].filter(x => x.value).map(x => (
            <div key={x.label} style={s.updItem}>
              <span style={s.updLabel}>{x.label}</span>
              <span style={s.updValue}>{x.value}</span>
            </div>
          ))}
        </div>
      )}

      {(data.unlockRisk || data.score) && (
        <div style={s.riskRow}>
          {data.unlockRisk && (
            <div style={s.riskBox}>
              <span style={s.riskLabel}>⚠️ {t('tokenomicsCard.unlockRisk')}</span>
              <span style={s.riskValue}>{data.unlockRisk}</span>
            </div>
          )}
          {data.score && (
            <div style={{ ...s.scoreBadge, color: scoreColor, borderColor: scoreColor + '40', background: scoreColor + '12' }}>
              {data.score.replace(/🟢|🟡|🔴/g, '').trim()}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const s = {
  supplyRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px', marginBottom: 12 },
  supplyItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  supplyLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.7 },
  supplyValue: { fontSize: 13, fontWeight: 700, color: 'var(--text)' },
  distRow: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14 },
  legend: { flex: 1, display: 'flex', flexDirection: 'column', gap: 5 },
  legendRow: { display: 'flex', alignItems: 'center', gap: 7 },
  legendDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontSize: 11, color: 'var(--text-muted)', flex: 1 },
  legendPct: { fontSize: 11, fontWeight: 700, color: 'var(--text)' },
  vestTable: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  vestRow: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  vestLeft: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 120 },
  vestDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 1 },
  vestLabel: { fontSize: 11, color: 'var(--text-muted)' },
  vestRight: { flex: 1, display: 'flex', flexDirection: 'column', gap: 3 },
  vestPct: { fontSize: 11, fontWeight: 700, color: 'var(--text)' },
  vestVesting: { fontSize: 10, color: 'var(--text-dim)' },
  utilRow: { marginTop: 6 },
  updateRow: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 },
  updItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  updLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 },
  updValue: { fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.55, wordBreak: 'break-word', overflowWrap: 'break-word' },
  riskRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' },
  riskBox: { flex: 1, background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '8px 12px' },
  riskLabel: { fontSize: 10, color: '#dc2626', fontWeight: 700, display: 'block', marginBottom: 2 },
  riskValue: { fontSize: 12, color: '#b91c1c' },
  scoreBadge: { fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 8, border: '1px solid' },
};
